import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Table_Borders = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🛠️ CSS Table Borders</h1>

      <p className="tutorial-intro">
        By default, HTML tables have no borders. With CSS, you can fully
        customize table borders — including width, color, spacing, and even
        collapsing them into a single border.
      </p>

      {/* Step 1: Simple Table Border */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Basic Table Border</h2>
        <p>
          To add a simple border to a table, rows, and cells, use the{" "}
          <code>border</code> property:
        </p>

        <CodeCopyBlock
          code={`table, th, td {
  border: 1px solid black;
}`}
        />

        <table style={{ border: "1px solid black", marginTop: "1rem" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid black", padding: "8px" }}>
                Name
              </th>
              <th style={{ border: "1px solid black", padding: "8px" }}>Age</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: "1px solid black", padding: "8px" }}>
                Riya
              </td>
              <td style={{ border: "1px solid black", padding: "8px" }}>22</td>
            </tr>
            <tr>
              <td style={{ border: "1px solid black", padding: "8px" }}>
                Aman
              </td>
              <td style={{ border: "1px solid black", padding: "8px" }}>25</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Step 2: Border Width */}
      <div className="tutorial-section">
        <h2 className="section-title">📏 Border Width</h2>
        <p>
          The <code>border-width</code> property defines how thick the border
          appears.
        </p>

        <CodeCopyBlock
          code={`th, td {
  border-width: 3px;
}`}
        />

        <table style={{ border: "1px solid black", marginTop: "1rem" }}>
          <tbody>
            <tr>
              <td style={{ border: "3px solid black", padding: "8px" }}>
                Thick Border
              </td>
              <td style={{ border: "3px solid black", padding: "8px" }}>
                Example
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Step 3: Border Style */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Border Style</h2>
        <p>
          The <code>border-style</code> property is required. It sets how the
          border looks: <code>solid</code>, <code>dashed</code>,{" "}
          <code>dotted</code>, etc.
        </p>

        <CodeCopyBlock
          code={`td {
  border: 2px dashed green;
}`}
        />

        <table style={{ border: "1px solid black", marginTop: "1rem" }}>
          <tbody>
            <tr>
              <td style={{ border: "2px dashed green", padding: "8px" }}>
                Dashed
              </td>
              <td style={{ border: "2px dotted blue", padding: "8px" }}>
                Dotted
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Step 4: Border Color */}
      <div className="tutorial-section">
        <h2 className="section-title">🌈 Border Color</h2>
        <p>
          The <code>border-color</code> property lets you define custom colors
          for table borders.
        </p>

        <CodeCopyBlock
          code={`td {
  border: 2px solid red;
}`}
        />

        <table style={{ border: "1px solid black", marginTop: "1rem" }}>
          <tbody>
            <tr>
              <td style={{ border: "2px solid red", padding: "8px" }}>Red</td>
              <td style={{ border: "2px solid purple", padding: "8px" }}>
                Purple
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Step 5: Collapse Borders */}
      <div className="tutorial-section">
        <h2 className="section-title">📐 Collapse Borders</h2>
        <p>
          By default, table cells have <b>separate borders</b>. To merge them
          into a single border, use <code>border-collapse: collapse;</code>.
        </p>

        <CodeCopyBlock
          code={`table {
  border-collapse: collapse;
}`}
        />

        <table
          style={{
            border: "1px solid black",
            borderCollapse: "collapse",
            marginTop: "1rem",
          }}
        >
          <tbody>
            <tr>
              <td style={{ border: "1px solid black", padding: "8px" }}>
                Collapsed
              </td>
              <td style={{ border: "1px solid black", padding: "8px" }}>
                Borders
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Step 6: Border Spacing */}
      <div className="tutorial-section">
        <h2 className="section-title">↔️ Border Spacing</h2>
        <p>
          The <code>border-spacing</code> property controls the space between
          table cells (works only if <code>border-collapse</code> is set to{" "}
          <code>separate</code>).
        </p>

        <CodeCopyBlock
          code={`table {
  border-spacing: 15px;
}`}
        />

        <table
          style={{
            border: "1px solid black",
            borderCollapse: "separate",
            borderSpacing: "15px",
            marginTop: "1rem",
          }}
        >
          <tbody>
            <tr>
              <td style={{ border: "1px solid black", padding: "8px" }}>
                Cell 1
              </td>
              <td style={{ border: "1px solid black", padding: "8px" }}>
                Cell 2
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Step 7: Table Padding */}
      <div className="tutorial-section">
        <h2 className="section-title">📦 Table Padding</h2>
        <p>
          The <code>padding</code> property controls the space inside each cell
          between the text and the border.
        </p>

        <CodeCopyBlock
          code={`td {
  padding: 20px;
}`}
        />

        <table
          style={{
            border: "1px solid black",
            borderCollapse: "collapse",
            marginTop: "1rem",
          }}
        >
          <tbody>
            <tr>
              <td style={{ border: "1px solid black", padding: "20px" }}>
                Padded Cell
              </td>
              <td style={{ border: "1px solid black", padding: "5px" }}>
                Small Padding
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Step 8: Outside Borders Only */}
      <div className="tutorial-section">
        <h2 className="section-title">⬛ Outside Table Borders</h2>
        <p>
          You can apply a border only to the entire table, without adding
          borders to individual cells.
        </p>

        <CodeCopyBlock
          code={`table {
  border: 3px solid green;
}

td {
  border: none;
}`}
        />

        <table
          style={{
            border: "3px solid green",
            borderCollapse: "collapse",
            marginTop: "1rem",
          }}
        >
          <tbody>
            <tr>
              <td style={{ padding: "10px" }}>Only Outside</td>
              <td style={{ padding: "10px" }}>Borders Visible</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table_Borders;
